import React from 'react';
import PuckColorField from './PuckColorField.jsx';
import InspectorRow from './InspectorRow';
import { usePuckRichTextStyle } from '../../context/PuckRichTextStyleContext';
import { useSelectedItemProps } from './useSelectedItemProps';
import { usePuckSiteTheme, sitePrimaryColor } from '../PuckSiteThemeContext';

/**
 * Corps du champ. Quand la couleur est héritée (le proposal l'a posée à la
 * génération), le bloc n'a pas de valeur à lui : le champ affiche alors la
 * couleur réellement rendue, lue sur le bloc sélectionné, plutôt qu'un blanc
 * que personne n'a choisi.
 */
function ColorFieldBody({
  value, onChange, allowTransparent, emptyIsInherited, readInherited, glyph,
}) {
  const { itemProps } = useSelectedItemProps();
  const { proposalStyles } = usePuckRichTextStyle();
  const siteTheme = usePuckSiteTheme();
  const inherited = emptyIsInherited && readInherited
    ? readInherited(itemProps, { proposalStyles, siteTheme })
    : '';
  return (
    <PuckColorFieldWithPalette
      label={null}
      value={value || inherited || ''}
      onChange={onChange}
      allowTransparent={allowTransparent}
      emptyIsInherited={emptyIsInherited}
      glyph={glyph}
    />
  );
}

export function PuckColorFieldWithPalette({
  label, value, onChange, allowTransparent, emptyIsInherited, glyph,
}) {
  const { swatches } = usePuckRichTextStyle();
  const swatchColors = swatches.map((s) => s.value).filter(Boolean);
  return (
    <PuckColorField
      label={label}
      value={allowTransparent || emptyIsInherited ? value : (value || '#ffffff')}
      onChange={onChange}
      size="xs"
      swatches={swatchColors}
      allowTransparent={allowTransparent}
      emptyIsInherited={emptyIsInherited}
      glyph={glyph}
    />
  );
}

/**
 * Champ Puck — couleur unifiée : bouton-icône qui ouvre le picker Mantine,
 * le nuancier de la palette proposal (`richTextPalette`) et l'option
 * transparent. Libellé rendu par InspectorRow, à gauche du bouton comme dans
 * le classique (label={null} côté PuckColorField).
 */
export function puckColorField(label, {
  allowTransparent = false, description, emptyIsInherited = false, readInherited, glyph,
} = {}) {
  return {
    type: 'custom',
    label,
    render: ({ onChange, value }) => (
      <InspectorRow label={label} description={description}>
        <ColorFieldBody
          value={value}
          onChange={onChange}
          allowTransparent={allowTransparent}
          emptyIsInherited={emptyIsInherited}
          readInherited={readInherited}
          glyph={glyph}
        />
      </InspectorRow>
    ),
  };
}

/** Couleur qui, vide, suit la couleur principale du site : le champ montre celle que le canvas applique, pas un blanc. */
export const puckPrimaryColorField = (label) => puckColorField(label, {
  emptyIsInherited: true,
  readInherited: (_props, { siteTheme }) => sitePrimaryColor(siteTheme) || '',
});

/** Couleur réglée, sinon la couleur principale du site (`transparent` : le champ « héritée » vidé). */
export const accentOrPrimary = (accent) => (accent && accent !== 'transparent' ? accent : 'var(--mantine-primary-color-filled)');
